import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import SEO, { getBreadcrumbSchema } from '../components/SEO';
import { Briefcase, Search, MapPin, Plus, DollarSign, Clock, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsList = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    employment_type: searchParams.get('employment_type') || '',
    search: searchParams.get('search') || '',
  });

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [catRes, empRes] = await Promise.all([
          fetch(`${API}/jobs/categories`),
          fetch(`${API}/jobs/employment-types`)
        ]);
        if (catRes.ok) setCategories(await catRes.json());
        if (empRes.ok) setEmploymentTypes(await empRes.json());
      } catch (err) {
        console.error('Error fetching meta:', err);
      }
    };
    fetchMeta();
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category) params.append('category', filters.category);
        if (filters.location) params.append('location', filters.location);
        if (filters.employment_type) params.append('employment_type', filters.employment_type);
        if (filters.search) params.append('search', filters.search);
        
        const res = await fetch(`${API}/jobs?${params}`);
        if (res.ok) {
          const data = await res.json();
          setJobs(data.jobs || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const formatSalary = (min, max, type) => {
    const format = (n) => new Intl.NumberFormat('pl-PL').format(n);
    const suffix = type === 'hourly' ? '/godz.' : type === 'daily' ? '/dzień' : '/mies.';
    
    if (max && max !== min) {
      return `${format(min)} - ${format(max)} PLN${suffix}`;
    }
    return `${format(min)} PLN${suffix}`;
  };

  const getEmploymentTypeName = (id) => {
    const type = employmentTypes.find(t => t.id === id);
    return type ? type.name : id;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-list-page">
      <SEO 
        title="Giełda Pracy - praca w budownictwie"
        description="Znajdź pracę w branży budowlanej. Oferty dla murarzy, elektryków, hydraulików, kierowników budowy i więcej."
        canonical="/jobs"
        structuredData={getBreadcrumbSchema([
          { name: 'Strona główna', path: '/' },
          { name: 'Giełda Pracy', path: '/jobs' },
        ])}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl text-white">Giełda Pracy</h1>
              <p className="text-violet-100">Praca w budownictwie</p>
            </div>
          </div>
          
          {isAuthenticated && (
            <Button 
              onClick={() => navigate('/jobs/new')} 
              className="bg-white text-violet-700 hover:bg-violet-50 font-semibold"
              data-testid="add-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Dodaj ofertę pracy
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Szukaj ofert..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? '' : v)}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Stanowisko" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie stanowiska</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filters.employment_type || "all"} onValueChange={(v) => handleFilterChange('employment_type', v === "all" ? '' : v)}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder="Typ zatrudnienia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie typy</SelectItem>
                  {employmentTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Lokalizacja..."
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="pl-10"
                  data-testid="location-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results count */}
        <p className="text-sm text-slate-500 mb-4">
          Znaleziono <strong>{total}</strong> ofert pracy
        </p>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-slate-500 mt-4">Ładowanie...</p>
          </div>
        ) : jobs.length === 0 ? (
          <Card className="text-center py-12">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-lg text-slate-700 mb-2">Brak ofert pracy</h3>
            <p className="text-slate-500 mb-4">Nie znaleziono ofert spełniających kryteria</p>
            {isAuthenticated && (
              <Button onClick={() => navigate('/jobs/new')} className="bg-violet-500 hover:bg-violet-600">
                Dodaj pierwszą ofertę
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map(job => (
              <Link key={job.job_id} to={`/jobs/${job.job_id}`}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-xl text-slate-900 group-hover:text-violet-600 transition-colors">
                            {job.title}
                          </h3>
                          <Badge variant="outline" className="border-violet-200 text-violet-700">
                            {getEmploymentTypeName(job.employment_type)}
                          </Badge>
                        </div>
                        
                        <p className="text-slate-600 mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {job.company_name || 'Pracodawca'}
                        </p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-violet-500" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-green-500" />
                            {formatSalary(job.salary_min, job.salary_max, job.salary_type)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">
                          {formatSalary(job.salary_min, job.salary_max, job.salary_type)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* CTA for non-authenticated - Only companies can post jobs */}
        {!isAuthenticated && (
          <Card className="mt-8 bg-gradient-to-r from-violet-500 to-purple-600 text-white">
            <CardContent className="p-6 text-center">
              <h3 className="font-heading font-bold text-xl mb-2">Szukasz pracowników budowlanych?</h3>
              <p className="text-violet-100 mb-4">Zarejestruj firmę i dodaj ofertę pracy za darmo</p>
              <Button onClick={() => navigate('/register?type=company')} className="bg-white text-violet-700 hover:bg-violet-50">
                <Building2 className="w-4 h-4 mr-2" />
                Zarejestruj firmę
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default JobsList;
